import { useForm, type UseFormRegister } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { AuthLayout, BackToSignInLink } from '@/components/layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { signUpSchema } from '@/schemas';
import { RoutePath } from '@/routes';
import { cn } from '@/lib/utils';

type SignUpFormValues = z.infer<typeof signUpSchema>;

// AUT-39: WEB-005. `signUpSchema`'s single `password` field is derived from
// the shared `passwordRules` list (decisions/password-policy-has-one-source-in-the-spa.md),
// but this sentence deliberately is not -- it is one line of designed prose,
// normative verbatim (signup.jpg), not the concatenation of three checklist
// labels. It sits directly above the schema's use below so the two are read
// together.
const PASSWORD_HELPER = 'Use at least 8 characters with a number and a symbol.';

type SignUpFieldName = keyof SignUpFormValues;

interface FieldProps {
  id: SignUpFieldName;
  label: string;
  type?: string;
  placeholder?: string;
  register: UseFormRegister<SignUpFormValues>;
  error?: string;
  helperText?: string;
}

// AUT-39: local to this screen, matching the per-file pattern already used
// for auth-screen fields (`ResetPassword.tsx`'s `RequirementItem`) rather
// than a shared abstraction with one caller. Owns the label/input/error/
// helper wiring once so the five fields below don't repeat it, and the
// aria-invalid / aria-describedby pairing required by
// `components/account.md § Accessibility and responsiveness`.
const Field = ({ id, label, type = 'text', placeholder, register, error, helperText }: FieldProps) => {
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const describedBy = [error && errorId, helperText && helperId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="w-full space-y-2">
      <Label
        htmlFor={id}
        className={cn('block text-sm leading-none', error && 'text-destructive')}
      >
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={cn(error && 'border-destructive focus-visible:ring-destructive')}
        {...register(id)}
      />
      {error && (
        <p id={errorId} className="leading-none text-destructive text-sm">
          {error}
        </p>
      )}
      {helperText && (
        <p id={helperId} className="text-sm text-muted-foreground">
          {helperText}
        </p>
      )}
    </div>
  );
};

// AUT-39: WEB-005. `/signup` inside the shared AuthLayout (WEB-001) --
// components/account.md § "/signup -- the screen that does nothing, on
// purpose". Membership is invite-only (JTBDs.md § JTBD-9); there is no
// registration endpoint and none is being built here
// (account-journeys.md § Built, but not a journey). The form validates on
// the client and a valid submit does nothing at all -- no request, no
// navigation, no toast, no state change beyond react-hook-form keeping the
// values the user typed. The submit button is never disabled: a
// permanently disabled button was considered and rejected (diverges from
// the capture, reads as broken).
const SignUp = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
  });

  const onSubmit = () => {
    // Intentionally inert -- see the block comment above.
  };

  return (
    <AuthLayout variant="centered">
      <BackToSignInLink />

      <h2 className="text-2xl font-semibold text-foreground">Create your account</h2>
      <p className="mt-2 text-sm text-muted-foreground">Set up your Signal workspace in a couple of steps.</p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            id="first_name"
            label="First name"
            register={register}
            error={errors.first_name?.message}
          />
          <Field
            id="last_name"
            label="Last name"
            register={register}
            error={errors.last_name?.message}
          />
        </div>

        <Field id="company" label="Company" register={register} error={errors.company?.message} />

        <Field
          id="email"
          label="Work email"
          type="email"
          placeholder="you@brand.com"
          register={register}
          error={errors.email?.message}
        />

        <Field
          id="password"
          label="Password"
          type="password"
          placeholder="••••••••"
          register={register}
          error={errors.password?.message}
          helperText={PASSWORD_HELPER}
        />

        <Button type="submit" className="w-full">
          Create account
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to={RoutePath.Login} className="font-medium text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
};

export default SignUp;
